import React, { useState } from 'react';

interface DropdownItem {
  label: string;
  value: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

interface DropdownProps {
  items: DropdownItem[];
  value?: string;
  placeholder?: string;
  onSelect: (value: string) => void;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
}

const Dropdown: React.FC<DropdownProps> = ({
  items,
  value,
  placeholder = 'Select an option',
  onSelect,
  disabled = false,
  className = '',
  style = {},
  size = 'md',
  fullWidth = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedItem = items.find(item => item.value === value);

  const getSizeClasses = (): string => {
    switch (size) {
      case 'sm':
        return 'h-8 px-2 text-sm';
      case 'lg':
        return 'h-12 px-4 text-lg';
      default:
        return 'h-10 px-3';
    }
  };

  const handleSelect = (itemValue: string) => {
    onSelect(itemValue);
    setIsOpen(false);
  };

  const dropdownClasses = ['relative', fullWidth ? 'w-full' : 'w-48', className]
    .filter(Boolean)
    .join(' ');

  const buttonClasses = [
    'flex items-center justify-between w-full border rounded-lg transition-colors',
    getSizeClasses(),
    disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const buttonStyles: React.CSSProperties = {
    backgroundColor: 'var(--bg-primary)',
    borderColor: 'var(--border)',
    color: 'var(--text-primary)',
    ...style,
  };

  return (
    <div className={dropdownClasses}>
      <button
        type="button"
        className={buttonClasses}
        style={buttonStyles}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onFocus={e => {
          if (!disabled) {
            e.currentTarget.style.borderColor = 'var(--accent)';
            e.currentTarget.style.boxShadow = '0 0 0 2px var(--accent)20';
          }
        }}
        onBlur={e => {
          if (!disabled) {
            e.currentTarget.style.borderColor = 'var(--border)';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
        onMouseEnter={e => {
          if (!disabled) {
            e.currentTarget.style.borderColor = 'var(--accent)';
          }
        }}
        onMouseLeave={e => {
          if (!disabled && !isOpen) {
            e.currentTarget.style.borderColor = 'var(--border)';
          }
        }}
        disabled={disabled}
      >
        <div className="flex items-center gap-2">
          {selectedItem?.icon && selectedItem.icon}
          <span
            style={{
              color: selectedItem
                ? 'var(--text-primary)'
                : 'var(--text-secondary)',
            }}
          >
            {selectedItem ? selectedItem.label : placeholder}
          </span>
        </div>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          style={{ color: 'var(--text-secondary)' }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown menu */}
          <div
            className="absolute top-full mt-1 w-full border rounded-lg shadow-lg z-20 py-1 max-h-60 overflow-y-auto overflow-x-hidden"
            style={{
              backgroundColor: 'var(--bg-primary)',
              borderColor: 'var(--border)',
            }}
          >
            {items.map(item => (
              <button
                key={item.value}
                type="button"
                className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${
                  item.disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-secondary cursor-pointer'
                } whitespace-nowrap overflow-hidden text-ellipsis`}
                style={{
                  color: item.disabled
                    ? 'var(--text-tertiary)'
                    : 'var(--text-primary)',
                }}
                onClick={() => !item.disabled && handleSelect(item.value)}
                disabled={item.disabled}
                onMouseEnter={e => {
                  if (!item.disabled) {
                    e.currentTarget.style.backgroundColor =
                      'var(--bg-secondary)';
                  }
                }}
                onMouseLeave={e => {
                  if (!item.disabled) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {item.icon && (
                  <span className="flex-shrink-0">{item.icon}</span>
                )}
                <span className="flex-1 truncate">{item.label}</span>
                {value === item.value && (
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: 'var(--accent)' }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Dropdown;
